import FilterButton from 'components/Button/FilterButton';
import DropdownMenu, { MenuLink } from 'components/Menu/Menu';
import Button from 'components/Button/Button';

import { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import './FilterNavigation.scss';
import { FormInput } from 'components/FormInput/FormInput';

interface FilterType {
  path: string;
  label: string;
  searchText: string;
}

interface FilterNavigationProps {
  /** An array of objects containing the path and label  */
  filters: FilterType[];
  onSortChange?: (label: string) => void;
  onSearchChange?: (searchText: string) => void;
  sortOptions?: MenuLink[];
  hideSort?: boolean;
  /**
   * 'live' (default): emit onSearchChange on every keystroke.
   * 'submit': only emit onSearchSubmit when the user clicks the Search button
   *           or presses Enter. onSearchChange is still emitted on keystroke
   *           so callers can stay informed of the typed value if they want.
   */
  searchMode?: 'live' | 'submit';
  onSearchSubmit?: (searchText: string) => void;
  searchButtonLabel?: string;
  /**
   * When true, on small screens only the search input and search button are
   * shown. The Clear button, sort dropdown and sub-menu filter dropdown are
   * hidden.
   */
  hideExtraOnMobile?: boolean;
}

/** A UI component to render the filter navigation */
export default function FilterNavigation({
  filters,
  hideSort,
  onSortChange,
  onSearchChange,
  sortOptions,
  searchMode = 'live',
  onSearchSubmit,
  searchButtonLabel = 'Search',
  hideExtraOnMobile = false,
}: FilterNavigationProps) {
  const [searchText, setSearchText] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('Latest');
  const [selectedSubMenuLabel, setSelectedSubMenuLabel] = useState('All');
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);

  function handleSorting(label: string) {
    setSelectedLabel(label);
    if (onSortChange) onSortChange(label);
  }

  function handleSearch(e: ChangeEvent<HTMLInputElement>) {
    setSearchText(e.target.value);
    if (onSearchChange) onSearchChange(e.target.value);
  }

  function handleSubmit() {
    if (onSearchSubmit) onSearchSubmit(searchText);
  }

  function handleClear() {
    setSearchText('');
    if (onSearchChange) onSearchChange('');
    if (onSearchSubmit) onSearchSubmit('');
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (searchMode === 'submit' && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  }

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div className={`filter${hideExtraOnMobile ? ' filter--hide-extra-on-mobile' : ''}`}>
      {
        isMobileView ?
          (
            <>
              <div className='dropdown-menu'>
                <form
                  className='filter-search-form'
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (searchMode === 'submit') handleSubmit();
                  }}
                >
                  <FormInput
                    id='Search'
                    label='Search'
                    onChange={handleSearch}
                    value={searchText}
                  />
                  {searchMode === 'submit' && (
                    <>
                      <Button
                        label={searchButtonLabel}
                        onClick={handleSubmit}
                      />
                      <span className='filter-clear-button'>
                        <Button
                          label='Clear'
                          secondary
                          onClick={handleClear}
                        />
                      </span>
                    </>
                  )}
                </form>
              </div>
            </>
          ) :
          (
            <>
              <nav className='filter-nav' aria-label='filter navigation'>
                {filters.map((filter) => (
                  <NavLink
                    key={filter.label}
                    to={filter.path}
                    className={({ isActive }) => (isActive ? 'filter-nav-active' : '')}
                  >
                    {filter.label}
                  </NavLink>
                ))}
              </nav>
              <form
                className='filter-search-form'
                onSubmit={(e) => {
                  e.preventDefault();
                  if (searchMode === 'submit') handleSubmit();
                }}
              >
                <FormInput
                  id='Search'
                  label='Search'
                  onChange={handleSearch}
                  value={searchText}
                />
                {searchMode === 'submit' && (
                  <>
                    <Button
                      label={searchButtonLabel}
                      onClick={handleSubmit}
                    />
                    <Button
                      label='Clear'
                      secondary
                      onClick={handleClear}
                    />
                  </>
                )}
              </form>
            </>
          )
      }

      {sortOptions && (
        <div className='filter-nav__menu' style={{ opacity: hideSort ? 0 : 1 }}>
          {
            isMobileView && (
              <div style = {{
                marginBottom: 5,
              }}>
                <DropdownMenu
                  links={filters}
                  buttonComponent={
                    <FilterButton label={selectedSubMenuLabel}/>
                  }
                  onMenuClick={(label) => {setSelectedSubMenuLabel(label)}}
                  selectedLabel={selectedSubMenuLabel}
                />
              </div>
            )
          }
          <DropdownMenu
            links={sortOptions}
            buttonComponent={<FilterButton label={selectedLabel} />}
            onMenuClick={(label) => handleSorting(label)}
            selectedLabel={selectedLabel}
          />
        </div>
      )}
    </div>
  );
}
